import { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, CheckCircleIcon, ExclamationTriangleIcon, MapPinIcon } from '@heroicons/react/24/outline';
import { Card, CardBody, Button, Input, Spinner } from '@components/ui';
import {
  MODAL_BACKDROP,
  MODAL_BODY,
  MODAL_CLOSE_X,
  MODAL_FOOT,
  MODAL_HEAD,
  MODAL_ICON_TILE,
  MODAL_KICKER,
  MODAL_PANEL,
  MODAL_TITLE,
} from '@components/ui/designTokens';
import { cn } from '@lib/utils/cn';
import { useGeolocation } from '@features/customers/hooks/useGeolocation';
import type { Address } from '@features/customers/types/customer.types';
import { invoiceService } from '@features/invoices/services/invoiceService';
import { showError } from '@/utils/sweetAlert';

interface AddressModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (addressData: AddressData) => void;
  initialData?: Partial<AddressData>;
  customerAddresses?: Address[]; // Direcciones existentes del cliente
  receiverType?: string; // DGI B401: 01=Contribuyente, 02=Consumidor Final, 03=Gobierno, 04=Extranjero
}

interface AddressData {
  country: string;
  countryCode?: string; // Código ISO del país (ej: PA, AR, US)
  province: string;
  district: string;
  township: string;
  description: string;
  fiscalCode?: string;
  city?: string;
}

export function AddressModal({ isOpen, onClose, onSave, initialData, customerAddresses, receiverType }: AddressModalProps) {
  const [showExisting, setShowExisting] = useState(true);
  const [isLoadingLocationCode, setIsLoadingLocationCode] = useState(false);
  const [locationCodeError, setLocationCodeError] = useState<string | null>(null);

  // DGI B405/B410: Determinar flujo de dirección según tipo de receptor
  const isExtranjero = receiverType === '04';
  const isConsumidorFinal = receiverType === '02';
  // 01 (Contribuyente) y 03 (Gobierno): requieren ubicación panameña completa
  const needsFullPanamaLocation = receiverType === '01' || receiverType === '03';

  const [formData, setFormData] = useState<AddressData>({
    // Para 01/02/03: forzar PANAMÁ. Para 04: cualquier país (incluyendo Panamá)
    country: isExtranjero
      ? (initialData?.country || '')
      : (initialData?.country || 'PANAMÁ'),
    countryCode: isExtranjero
      ? (initialData?.countryCode || '')
      : (initialData?.countryCode || 'PA'),
    province: initialData?.province || '',
    district: initialData?.district || '',
    township: initialData?.township || '',
    description: initialData?.description || '',
    fiscalCode: initialData?.fiscalCode || '',
    city: initialData?.city || '',
  });

  // Normalizar texto eliminando acentos para comparaciones flexibles
  const normalize = (text: string) => text?.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase() || '';

  // Use geolocation hooks
  const { countries, provinces, districts, townships, isLoadingProvinces, isLoadingDistricts, isLoadingTownships } = useGeolocation(
    formData.province,
    formData.district
  );

  // Corregir nombres de provincia/distrito/corregimiento que vengan sin acentos (ej: al clonar factura)
  // El forward mapper aplica normalizeAccents() al enviar, así que los nombres en el payload pueden
  // venir sin acentos ("Panama" en vez de "Panamá"), lo que causa que los dropdowns no matcheen.
  useEffect(() => {
    if (!provinces || provinces.length === 0 || !formData.province) return;

    // Si la provincia ya matchea exacto, no hacer nada
    const exactMatch = provinces.find((p: any) => p.Name === formData.province);
    if (exactMatch) return;

    // Buscar por nombre normalizado (sin acentos)
    const normalizedMatch = provinces.find(
      (p: any) => normalize(p.Name) === normalize(formData.province)
    );
    if (normalizedMatch && normalizedMatch.Name !== formData.province) {
      console.log(`🔄 Corrigiendo provincia: "${formData.province}" → "${normalizedMatch.Name}"`);
      setFormData(prev => ({ ...prev, province: normalizedMatch.Name }));
    }
  }, [provinces, formData.province]);

  useEffect(() => {
    if (!districts || districts.length === 0 || !formData.district) return;

    const exactMatch = districts.find((d: any) => d.Name === formData.district);
    if (exactMatch) return;

    const normalizedMatch = districts.find(
      (d: any) => normalize(d.Name) === normalize(formData.district)
    );
    if (normalizedMatch && normalizedMatch.Name !== formData.district) {
      console.log(`🔄 Corrigiendo distrito: "${formData.district}" → "${normalizedMatch.Name}"`);
      setFormData(prev => ({ ...prev, district: normalizedMatch.Name }));
    }
  }, [districts, formData.district]);

  useEffect(() => {
    if (!townships || townships.length === 0 || !formData.township) return;

    const exactMatch = townships.find((t: any) => t.Name === formData.township);
    if (exactMatch) return;

    const normalizedMatch = townships.find(
      (t: any) => normalize(t.Name) === normalize(formData.township)
    );
    if (normalizedMatch && normalizedMatch.Name !== formData.township) {
      console.log(`🔄 Corrigiendo corregimiento: "${formData.township}" → "${normalizedMatch.Name}"`);
      setFormData(prev => ({ ...prev, township: normalizedMatch.Name }));
    }
  }, [townships, formData.township]);

  const isPANAMÁ = formData.country?.toUpperCase() === 'PANAMÁ' || formData.country?.toUpperCase() === 'PANAMÁ';

  // Auto-fetch location code when province, district, and township are all selected
  useEffect(() => {
    const fetchLocationCode = async () => {
      // Only fetch if it's Panama and all three fields are filled
      if (!isPANAMÁ || !formData.province || !formData.district || !formData.township) {
        setLocationCodeError(null);
        return;
      }

      // Clear previous error and fiscal code
      setLocationCodeError(null);
      setFormData((prev) => ({ ...prev, fiscalCode: '' }));
      setIsLoadingLocationCode(true);

      try {
        console.log('📡 Auto-buscando código de ubicación fiscal para:', {
          province: formData.province,
          district: formData.district,
          township: formData.township,
        });

        const locationData = await invoiceService.getLocationCode({
          province: formData.province,
          district: formData.district,
          township: formData.township,
        });

        console.log('✅ Código de ubicación fiscal obtenido:', locationData);

        setFormData((prev) => ({
          ...prev,
          fiscalCode: locationData.code,
        }));
        setLocationCodeError(null);
      } catch (error: any) {
        console.error('❌ Error al obtener código de ubicación:', error);

        const is404 = error?.response?.status === 404;
        const errorMessage = error?.response?.data?.message || error?.message || '';

        if (is404 || errorMessage.includes('not found')) {
          setLocationCodeError('El servicio de código de ubicación no está disponible');
        } else {
          setLocationCodeError('No se encontró código de ubicación para esta dirección');
        }
      } finally {
        setIsLoadingLocationCode(false);
      }
    };

    fetchLocationCode();
  }, [isPANAMÁ, formData.province, formData.district, formData.township]);

  const handleSave = () => {
    // Validar país seleccionado
    if (!formData.country) {
      showError('Campo Requerido', 'Debe seleccionar un país');
      return;
    }

    // DGI B410a (1611): Receptores nacionales (01/02/03) deben tener país = PA
    if (!isExtranjero && !isPANAMÁ) {
      showError('País Inválido', 'Para este tipo de receptor, el país debe ser Panamá (DGI B410a)');
      return;
    }

    // Receptor extranjero puede tener cualquier país (incluyendo PA si vive en Panamá)
    // La validación B410b (país ≠ PA si destino=Extranjero) se hace en el invoiceMapper

    // DGI B404: Descripción requerida para Contribuyente (01) y Gobierno (03), opcional para Consumidor Final (02) y Extranjero (04)
    if (needsFullPanamaLocation && !formData.description.trim()) {
      showError('Campo Requerido', 'Debe ingresar una descripción de la dirección');
      return;
    }

    // DGI B405: Ubicación completa requerida para Contribuyente (01) y Gobierno (03)
    if (needsFullPanamaLocation && isPANAMÁ) {
      if (!formData.province || !formData.district || !formData.township) {
        showError('Campos Requeridos', 'Debe seleccionar provincia, distrito y corregimiento (DGI B405)');
        return;
      }
    }

    // Ciudad es opcional para extranjeros

    // Para Consumidor Final: limpiar datos de ubicación que no deben enviarse (DGI B405 prohibido)
    if (isConsumidorFinal) {
      onSave({
        ...formData,
        province: '',
        district: '',
        township: '',
        fiscalCode: '',
      });
      return;
    }

    onSave(formData);
  };

  const updateField = (field: keyof AddressData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSelectExistingAddress = (address: Address) => {
    const addrCountry = address.country || '';
    const matchedCountry = countries?.find(
      (c) => c.Name.toUpperCase() === addrCountry.toUpperCase()
    );
    setFormData({
      // DGI B410a: receptores nacionales (01/02/03) siempre son PANAMÁ, aunque la
      // dirección guardada venga sin país (ej. cliente importado). El campo País es un
      // display fijo para estos tipos, así que forzar PA evita que handleSave bloquee
      // el guardado por país vacío sin darle al usuario forma de corregirlo.
      country: isExtranjero ? addrCountry : 'PANAMÁ',
      countryCode: isExtranjero ? (matchedCountry?.Code || '') : 'PA',
      province: address.state || '',
      district: address.district || '',
      township: address.town || '',
      description: address.line1 || '',
      fiscalCode: address.code_address || '',
      city: address.city || '',
    });
    setShowExisting(false);
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className={MODAL_BACKDROP} />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className={cn('flex max-h-[calc(100vh-2rem)] max-w-2xl flex-col', MODAL_PANEL)}
              >
                {/* ---------- HEAD ---------- */}
                <div className={cn(MODAL_HEAD, 'shrink-0')}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex min-w-0 items-center gap-3.5">
                      <span className={MODAL_ICON_TILE}>
                        <MapPinIcon />
                      </span>
                      <div className="min-w-0">
                        <div className={MODAL_KICKER}>Factura · Receptor</div>
                        <Dialog.Title as="h3" className={cn(MODAL_TITLE, 'mt-1')}>
                          Dirección del receptor
                        </Dialog.Title>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={onClose}
                      aria-label="Cerrar"
                      data-testid="invoice-wizard-address-modal-close-button"
                      className={MODAL_CLOSE_X}
                    >
                      <XMarkIcon className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>

                {/* ---------- BODY ---------- */}
                <div className={cn(MODAL_BODY, 'min-h-0 flex-1 space-y-4')}>
                  {/* Direcciones existentes del cliente */}
                  {customerAddresses && customerAddresses.length > 0 && showExisting && (
                    <Card>
                      <div className="bg-secondary-50 dark:bg-secondary-800 px-4 py-3 border-b border-secondary-200 dark:border-secondary-700">
                        <h4 className="text-sm font-semibold text-secondary-900 dark:text-white">
                          Direcciones guardadas del cliente
                        </h4>
                      </div>
                      <CardBody className="p-4">
                        <div className="space-y-2">
                          {customerAddresses.map((address, index) => (
                            <button
                              key={address._id || index}
                              onClick={() => handleSelectExistingAddress(address)}
                              data-testid={`invoice-wizard-address-modal-existing-address-${address._id || index}-button`}
                              className="w-full text-left p-3 rounded-lg border border-secondary-300 dark:border-secondary-600 hover:bg-secondary-50 dark:hover:bg-secondary-700 transition-colors"
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="text-xs font-semibold text-primary-600 dark:text-primary-400 uppercase">
                                      {address.type}
                                    </span>
                                    {address.is_default && (
                                      <span className="flex items-center gap-1 text-xs text-success-600 dark:text-success-400">
                                        <CheckCircleIcon className="w-3 h-3" />
                                        Por defecto
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-sm text-secondary-900 dark:text-white font-medium">
                                    {address.line1}
                                  </p>
                                  <p className="text-xs text-secondary-600 dark:text-secondary-400 mt-1">
                                    {[address.city, address.district, address.state, address.country]
                                      .filter(Boolean)
                                      .join(', ')}
                                  </p>
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                        <div className="mt-4 pt-4 border-t border-secondary-200 dark:border-secondary-700">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setShowExisting(false)}
                            data-testid="invoice-wizard-address-modal-create-new-button"
                            className="w-full"
                          >
                            Crear nueva dirección
                          </Button>
                        </div>
                      </CardBody>
                    </Card>
                  )}

                  {/* Formulario de dirección */}
                  {(!customerAddresses || customerAddresses.length === 0 || !showExisting) && (
                    <Card>
                      <CardBody>
                        <div className="space-y-4">
                          {/* País — filtrado según tipo de receptor DGI */}
                          <div>
                            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                              País <span className="text-danger-600">*</span>
                            </label>
                            {!isExtranjero ? (
                              /* 01/02/03: País fijo PANAMÁ (DGI B410a código 1611) */
                              <div className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-secondary-50 dark:bg-secondary-800 text-secondary-900 dark:text-white">
                                PANAMÁ
                              </div>
                            ) : (
                              /* 04: Todos los países (el extranjero puede estar en Panamá) */
                              <select data-testid="invoice-wizard-address-modal-country-select"
                                className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-700 text-secondary-900 dark:text-white"
                                value={formData.country}
                                onChange={(e) => {
                                  const selectedCountry = countries?.find((c) => c.Name === e.target.value);
                                  updateField('country', e.target.value);
                                  updateField('countryCode', selectedCountry?.Code || '');
                                  updateField('province', '');
                                  updateField('district', '');
                                  updateField('township', '');
                                  updateField('city', '');
                                }}
                              >
                                <option value="">Seleccione el país del receptor</option>
                                {countries
                                  ?.map((country, index) => (
                                    <option key={`country-${country.Id}-${index}`} value={country.Name}>
                                      {country.Name}
                                    </option>
                                  ))}
                              </select>
                            )}
                          </div>

                          {/* DGI B405: Provincia/Distrito/Corregimiento
                              - REQUERIDO para 01 (Contribuyente) y 03 (Gobierno)
                              - PROHIBIDO para 02 (Consumidor Final) y 04 (Extranjero) */}
                          {needsFullPanamaLocation && isPANAMÁ ? (
                            <>
                              {/* Provincia */}
                              <div>
                                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                                  Provincia <span className="text-danger-600">*</span>
                                </label>
                                <select data-testid="invoice-wizard-address-modal-province-select"
                                  className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-700 text-secondary-900 dark:text-white"
                                  value={formData.province}
                                  onChange={(e) => {
                                    updateField('province', e.target.value);
                                    updateField('district', '');
                                    updateField('township', '');
                                  }}
                                  disabled={isLoadingProvinces}
                                >
                                  <option value="">
                                    {isLoadingProvinces ? 'Cargando...' : 'Seleccione una provincia'}
                                  </option>
                                  {provinces?.map((province: any, index: number) => (
                                    <option key={`province-${province.ProvinceId}-${index}`} value={province.Name}>
                                      {province.Name}
                                    </option>
                                  ))}
                                </select>
                              </div>

                              {/* Distrito */}
                              <div>
                                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                                  Distrito <span className="text-danger-600">*</span>
                                </label>
                                <select data-testid="invoice-wizard-address-modal-district-select"
                                  className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-700 text-secondary-900 dark:text-white"
                                  value={formData.district}
                                  onChange={(e) => {
                                    updateField('district', e.target.value);
                                    updateField('township', '');
                                  }}
                                  disabled={!formData.province || isLoadingDistricts}
                                >
                                  <option value="">
                                    {isLoadingDistricts ? 'Cargando...' : 'Seleccione un distrito'}
                                  </option>
                                  {districts?.map((district: any, index: number) => (
                                    <option key={`district-${district.DistrictId}-${index}`} value={district.Name}>
                                      {district.Name}
                                    </option>
                                  ))}
                                </select>
                              </div>

                              {/* Corregimiento */}
                              <div>
                                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                                  Corregimiento <span className="text-danger-600">*</span>
                                </label>
                                <select data-testid="invoice-wizard-address-modal-township-select"
                                  className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-700 text-secondary-900 dark:text-white"
                                  value={formData.township}
                                  onChange={(e) => updateField('township', e.target.value)}
                                  disabled={!formData.district || isLoadingTownships}
                                >
                                  <option value="">
                                    {isLoadingTownships ? 'Cargando...' : 'Seleccione un corregimiento'}
                                  </option>
                                  {townships?.map((township: any, index: number) => (
                                    <option key={`township-${township.TownshipId}-${index}`} value={township.Name}>
                                      {township.Name}
                                    </option>
                                  ))}
                                </select>
                              </div>

                              {/* Estado del código de ubicación fiscal */}
                              {formData.province && formData.district && formData.township && (
                                <>
                                  {isLoadingLocationCode && (
                                    <div data-testid="invoice-wizard-address-modal-location-code-loading" className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                                      <div className="flex items-center gap-2">
                                        <Spinner className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                        <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                                          Buscando código de ubicación fiscal...
                                        </p>
                                      </div>
                                    </div>
                                  )}

                                  {!isLoadingLocationCode && formData.fiscalCode && (
                                    <div data-testid="invoice-wizard-address-modal-fiscal-code-alert" className="bg-success-50 dark:bg-success-900/20 border border-success-200 dark:border-success-800 rounded-lg p-3">
                                      <div className="flex items-center gap-2">
                                        <CheckCircleIcon className="w-5 h-5 text-success-600 dark:text-success-400" />
                                        <div>
                                          <p className="text-sm font-medium text-success-900 dark:text-success-100">
                                            Código de Ubicación Fiscal
                                          </p>
                                          <p className="text-lg font-bold text-success-700 dark:text-success-300">
                                            {formData.fiscalCode}
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                  {!isLoadingLocationCode && !formData.fiscalCode && locationCodeError && (
                                    <div data-testid="invoice-wizard-address-modal-location-code-error" className="bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-800 rounded-lg p-3">
                                      <div className="flex items-center gap-2">
                                        <ExclamationTriangleIcon className="w-5 h-5 text-warning-600 dark:text-warning-400" />
                                        <div>
                                          <p className="text-sm font-medium text-warning-900 dark:text-warning-100">
                                            {locationCodeError}
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </>
                              )}
                            </>
                          ) : isConsumidorFinal ? (
                            /* Consumidor Final: DGI B405 prohíbe gUbiRec — solo descripción */
                            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                              <p className="text-xs text-blue-700 dark:text-blue-300">
                                Para Consumidor Final no se requiere ubicación fiscal (provincia, distrito, corregimiento). Solo ingrese la descripción de la dirección.
                              </p>
                            </div>
                          ) : isExtranjero ? (
                            <>
                              {/* Extranjero: ciudad opcional */}
                              <div>
                                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                                  Ciudad <span className="text-secondary-400 text-xs ml-1">(Opcional)</span>
                                </label>
                                <Input
                                  value={formData.city || ''}
                                  onChange={(e) => updateField('city', e.target.value)}
                                  placeholder="Ciudad del receptor"
                                 data-testid="invoice-wizard-address-modal-city-input"/>
                              </div>
                            </>
                          ) : null}

                          {/* Descripción de la dirección */}
                          <div>
                            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                              Descripción de la dirección {needsFullPanamaLocation ? <span className="text-danger-600">*</span> : <span className="text-secondary-400 text-xs ml-1">(Opcional)</span>}
                            </label>
                            <textarea
                              className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-700 text-secondary-900 dark:text-white"
                              rows={3}
                              value={formData.description}
                              onChange={(e) => updateField('description', e.target.value)}
                              placeholder="Ej: Calle 50, Edificio Plaza 2000, Piso 10"
                              maxLength={100}
                             data-testid="invoice-wizard-address-modal-description-textarea"/>
                            <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-1">
                              Máximo 100 caracteres
                            </p>
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  )}

                </div>

                {/* ---------- FOOT ---------- */}
                <div className={cn(MODAL_FOOT, 'shrink-0')}>
                  <div>
                    {customerAddresses && customerAddresses.length > 0 && !showExisting && (
                      <Button variant="outline" onClick={() => setShowExisting(true)} data-testid="invoice-wizard-address-modal-show-existing-button">
                        Ver direcciones guardadas
                      </Button>
                    )}
                  </div>
                  <div className="ml-auto flex gap-2.5">
                    <Button variant="outline" onClick={onClose} data-testid="invoice-wizard-address-modal-cancel-button">
                      Cancelar
                    </Button>
                    <Button variant="primary" onClick={handleSave} data-testid="invoice-wizard-address-modal-save-button">
                      Guardar
                    </Button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
